/**
 * Advantages Section - Why Choose Dravonix
 * =========================================
 * 6 advantage cards with icons and descriptions
 */

import React, { useState, useEffect, useRef } from 'react';
import './Advantages.css';

const advantagesData = [
  {
    id: 1,
    icon: 'fa-shield-alt',
    title: 'Security First',
    description: 'Military-grade security protocols for every project.',
    color: '#00D4FF'
  },
  {
    id: 2,
    icon: 'fa-bolt',
    title: 'Rapid Delivery',
    description: 'Launch faster with our agile methodology.',
    color: '#D4A853'
  },
  {
    id: 3,
    icon: 'fa-users-cog',
    title: 'Expert Team',
    description: 'Blockchain veterans and growth hackers.',
    color: '#A855F7'
  },
  {
    id: 4,
    icon: 'fa-handshake',
    title: 'True Partnership',
    description: 'Long-term support and growth partnerships.',
    color: '#10B981'
  },
  {
    id: 5,
    icon: 'fa-layer-group',
    title: 'Full Stack',
    description: 'Blockchain to marketing - everything in one place.',
    color: '#F97316'
  },
  {
    id: 6,
    icon: 'fa-chart-line',
    title: 'Proven Results',
    description: '100% client satisfaction rate.',
    color: '#EC4899'
  }
];

// Particles Component
const AdvantagesParticles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const createParticles = () => {
      const count = window.innerWidth < 768 ? 40 : 80;
      particles = [];
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 0.5,
          speedX: (Math.random() - 0.5) * 0.3,
          speedY: (Math.random() - 0.5) * 0.3,
          opacity: Math.random() * 0.4 + 0.1
        });
      }
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(5, 5, 5, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;

        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 168, 83, ${p.opacity})`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    resize();
    createParticles();
    animate();

    window.addEventListener('resize', () => {
      resize();
      createParticles();
    });

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="advantages-canvas" />;
};

// Advantage Card Component
const AdvantageCard = ({ advantage, index, isInView }) => {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setIsVisible(true), index * 100);
      return () => clearTimeout(timer);
    }
  }, [isInView, index]);

  return (
    <div
      ref={cardRef}
      className={`advantage-card ${isVisible ? 'in-view' : ''}`}
    >
      <div className="advantage-glow" style={{ background: `radial-gradient(circle, ${advantage.color}15 0%, transparent 70%)` }} />
      
      <div className="advantage-icon-wrapper">
        <div className="advantage-ring" style={{ borderColor: `${advantage.color}40` }} />
        <div className="advantage-icon" style={{ color: advantage.color, background: `${advantage.color}15`, borderColor: `${advantage.color}30` }}>
          <i className={`fas ${advantage.icon}`} />
        </div>
      </div>
      
      <h3>{advantage.title}</h3>
      <p>{advantage.description}</p>
      
      <div className="advantage-corner advantage-corner-tl" style={{ borderColor: `${advantage.color}30` }} />
      <div className="advantage-corner advantage-corner-br" style={{ borderColor: `${advantage.color}30` }} />
    </div>
  );
};

const Advantages = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="advantages-section" ref={sectionRef} id="advantages">
      {/* Background - CSS particles */}
      <div className="advantages-bg">
        {/* Subtle glows */}
        <div className="advantages-glow advantages-glow-1" />
        <div className="advantages-glow advantages-glow-2" />
        
        {/* Particles - 20 bigger particles */}
        <div className="advantages-particles">
          {[...Array(20)].map((_, i) => (
            <span 
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                '--size': `${4 + Math.random() * 6}px`,
                '--duration': `${12 + Math.random() * 15}s`,
                '--delay': `${Math.random() * 8}s`
              }}
            />
          ))}
        </div>
        <div className="advantages-grid-bg" />
        <div className="advantages-vignette" />
      </div>

      <div className="advantages-container">
        {/* Header */}
        <div className={`advantages-header ${isInView ? 'in-view' : ''}`}>
          <div className="section-badge">
            <span className="badge-dot" />
            <span>Our Advantages</span>
          </div>
          <h2 className="section-title">
            Why <span className="text-highlight">Dravonix</span>
          </h2>
          <p className="section-subtitle">
            What sets us apart in the blockchain development space
          </p>
        </div>

        {/* Advantages Grid */}
        <div className="advantages-grid">
          {advantagesData.map((advantage, index) => (
            <AdvantageCard
              key={advantage.id}
              advantage={advantage}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Advantages;
